(() => {
  class PPHeroCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ppHeroReady) return;

      this.slides = [...this.querySelectorAll('[data-pp-hero-slide]')];
      this.dots = [...this.querySelectorAll('[data-pp-hero-dot]')];
      this.previousButton = this.querySelector('[data-pp-hero-previous]');
      this.nextButton = this.querySelector('[data-pp-hero-next]');
      this.pauseButton = this.querySelector('[data-pp-hero-pause]');
      this.pauseIcon = this.querySelector('[data-pp-hero-pause-icon]');
      this.status = this.querySelector('[data-pp-hero-status]');
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.dataset.state === 'active'));
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.delay = Math.max(5000, Math.min(10000, Number(this.dataset.delay) || 7000));
      this.userPaused = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.timer = null;
      this.pointerStart = null;

      this.previousButton?.addEventListener('click', () => this.show(this.index - 1, true));
      this.nextButton?.addEventListener('click', () => this.show(this.index + 1, true));
      this.pauseButton?.addEventListener('click', () => this.toggleAutoplay());
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.show(Number(dot.dataset.ppHeroDot), true)));

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.show(this.index - 1, true);
        if (event.key === 'ArrowRight') this.show(this.index + 1, true);
      });
      this.addEventListener('pointerdown', (event) => { this.pointerStart = event.clientX; }, { passive: true });
      this.addEventListener('pointerup', (event) => {
        if (this.pointerStart === null) return;
        const distance = event.clientX - this.pointerStart;
        this.pointerStart = null;
        if (Math.abs(distance) < 50) return;
        this.show(this.index + (distance < 0 ? 1 : -1), true);
      }, { passive: true });
      this.addEventListener('pointercancel', () => { this.pointerStart = null; }, { passive: true });

      document.addEventListener('visibilitychange', this.onVisibilityChange = () => {
        if (document.hidden) this.stopTimer(); else this.startTimer();
      });

      this.dataset.ppHeroReady = 'true';
      this.updatePauseButton();
      this.show(this.index, false);
      this.startTimer();
    }

    disconnectedCallback() {
      this.stopTimer();
      if (this.onVisibilityChange) document.removeEventListener('visibilitychange', this.onVisibilityChange);
    }

    show(nextIndex, announce = false) {
      if (!this.slides.length) return;
      this.index = (nextIndex + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.index;
        slide.dataset.state = active ? 'active' : (slideIndex < this.index ? 'before' : 'after');
        slide.setAttribute('aria-hidden', String(!active));
        if ('inert' in slide) slide.inert = !active;
      });
      this.dots.forEach((dot, dotIndex) => {
        if (dotIndex === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (announce && this.status) this.status.textContent = this.slides[this.index].getAttribute('aria-label') || '';
      this.restartTimer();
    }

    toggleAutoplay() {
      this.userPaused = !this.userPaused;
      this.updatePauseButton();
      if (this.userPaused) this.stopTimer(); else this.startTimer();
    }

    updatePauseButton() {
      if (!this.pauseButton) return;
      const label = this.userPaused ? this.pauseButton.dataset.labelPlay : this.pauseButton.dataset.labelPause;
      this.pauseButton.setAttribute('aria-label', label || '');
      if (this.pauseIcon) this.pauseIcon.textContent = this.userPaused ? '▶' : 'Ⅱ';
    }

    startTimer() {
      if (!this.autoplay || this.userPaused || document.hidden || this.timer) return;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        this.show(this.index + 1, false);
      }, this.delay);
    }

    stopTimer() {
      if (!this.timer) return;
      window.clearTimeout(this.timer);
      this.timer = null;
    }

    restartTimer() {
      this.stopTimer();
      this.startTimer();
    }
  }

  if (!customElements.get('pp-hero-carousel')) customElements.define('pp-hero-carousel', PPHeroCarousel);

  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-pp-hero-slide]');
    const hero = slide?.closest('pp-hero-carousel');
    if (!hero || typeof hero.show !== 'function') return;
    hero.userPaused = true;
    hero.updatePauseButton();
    hero.show(Number(slide.dataset.slideIndex), false);
  });
})();
